import { uid } from '../../../../lib/uid';
import type { EducationLevel } from '../personnelTypes';

/**
 * Test data backing the Autogenerate button on the education form.
 *
 * Fills every required field so the primary action enables on the first try — and the optional
 * ones the form can answer honestly, because "Save enables" is the floor rather than the whole
 * contract: a line generated with only its required fields renders a row of em dashes, which
 * exercises none of the columns behind it.
 *
 * Keep it in lockstep with the form. A new required field added there without a value here
 * silently breaks the button, and nothing fails.
 *
 * **A school is not a person, so this may pick plausible vocabulary** — which is the opposite
 * of the rule `samplePersonnel` follows. That generator writes a row into `rsys_pii` and must
 * read as obviously synthetic; nothing here leaves `rsys_`, and an unreadable school name would
 * make the generated profile worse at the one job it has. The names are still invented: no real
 * institution, and a `uid` suffix so repeated clicks do not produce identical rows.
 */
export function sampleEducation(): {
  level: EducationLevel;
  schoolName: string;
  degreeOrCourse: string;
  periodFrom: string;
  periodTo: string;
  yearGraduated: number;
  honors: string;
} {
  /*
    College, because it is the level that exercises every column — a degree, a graduation year
    and an honour. Elementary would leave three of them blank and the generated row would look
    like the bug where a section was never wired.
  */
  const level: EducationLevel = 4;

  /*
    A period that ENDS, and a graduation year inside it. The server refuses study that ends
    before it began and a qualification conferred before attendance started, so a generator
    that wandered into either would produce a form the reader cannot save — which reads as a
    bug in the form rather than in the test data.

    Anchored to a fixed, plainly historical span rather than to today: a window computed from
    `now` eventually drifts into a year the max refuses.
  */
  const periodFrom = '2010-06-01';
  const periodTo = '2014-03-31';

  return {
    level,
    schoolName: `Sample State University ${uid('').slice(0, 6).toUpperCase()}`,
    degreeOrCourse: 'Bachelor of Science in Public Administration',
    periodFrom,
    periodTo,
    yearGraduated: 2014,
    honors: 'Cum laude',
  };
}
